// Template-based completion message registry.
// No runtime AI generation — lines are picked deterministically-with-variation
// based on duration bucket and goal status.

export type CompletionType = "atmosphere" | "benefit";
export type CompletionBucket = "short" | "standard" | "goal_met" | "deep" | "interrupted";

export interface CompletionMessage {
  id: string;
  key: string;
  type: CompletionType;
  bucket: CompletionBucket;
  minDurationMinutes: number;
  maxDurationMinutes: number;
  requiresGoalMet: boolean;
  text: string;
  active: boolean;
}

const M = (
  id: string,
  type: CompletionType,
  bucket: CompletionBucket,
  range: [number, number],
  text: string,
  requiresGoalMet = false
): CompletionMessage => ({
  id,
  key: `${type}.${bucket}.${id}`,
  type,
  bucket,
  minDurationMinutes: range[0],
  maxDurationMinutes: range[1],
  requiresGoalMet,
  text,
  active: true,
});

// ----- ATMOSPHERE LINES (cosmic-horror flavored) -----
const ATMOSPHERE: CompletionMessage[] = [
  // short / interrupted
  M("atm-int-1", "atmosphere", "interrupted", [1, 240], "Signal terminated early. The instrument logs the silence."),
  M("atm-int-2", "atmosphere", "interrupted", [1, 240], "Window closed under threshold. The void notes the deviation."),
  M("atm-short-1", "atmosphere", "short", [1, 360], "Brief abstention recorded. The lattice barely shifts."),
  // standard
  M("atm-std-1", "atmosphere", "standard", [240, 960], "The vessel held its silence. Something on the other side was listening."),
  M("atm-std-2", "atmosphere", "standard", [240, 960], "Cellular machinery hummed in the dark. The reading is clean."),
  M("atm-std-3", "atmosphere", "standard", [240, 960], "Hours dissolved without intake. The protocol approves."),
  // goal_met
  M("atm-goal-1", "atmosphere", "goal_met", [1, 99999], "Target threshold breached. The signal stabilizes.", true),
  M("atm-goal-2", "atmosphere", "goal_met", [1, 99999], "Protocol satisfied. The instrument exhales.", true),
  M("atm-goal-3", "atmosphere", "goal_met", [1, 99999], "Window sealed at target. The watchers log it.", true),
  // deep
  M("atm-deep-1", "atmosphere", "deep", [1080, 99999], "Deep abstention recorded. Time begins to feel borrowed."),
  M("atm-deep-2", "atmosphere", "deep", [1080, 99999], "Extended void cycle complete. The body has been rewritten in small ways."),
];

// ----- BENEFIT LINES (consistency / structure / routine — no medical claims) -----
const BENEFIT: CompletionMessage[] = [
  M("ben-int-1", "benefit", "interrupted", [1, 240], "Short cycles still build the muscle of starting and stopping cleanly."),
  M("ben-short-1", "benefit", "short", [1, 360], "Every logged window reinforces the structure of your routine."),
  M("ben-std-1", "benefit", "standard", [240, 960], "Consistency, not heroics. Another window held inside your normal pattern."),
  M("ben-std-2", "benefit", "standard", [240, 960], "One more entry in the log. The shape of your week becomes more legible."),
  M("ben-std-3", "benefit", "standard", [240, 960], "Holding a window like this keeps your eating structure intact."),
  M("ben-goal-1", "benefit", "goal_met", [1, 99999], "Hitting target reinforces the schedule — easier to repeat tomorrow.", true),
  M("ben-goal-2", "benefit", "goal_met", [1, 99999], "Targets met become the default. The pattern is doing the work.", true),
  M("ben-deep-1", "benefit", "deep", [1080, 99999], "Long windows are best treated as occasional structure, not routine."),
  M("ben-deep-2", "benefit", "deep", [1080, 99999], "A long hold logged cleanly. Return to your normal cadence next."),
];

const ALL: CompletionMessage[] = [...ATMOSPHERE, ...BENEFIT].filter((m) => m.active);

function bucketFor(durationMin: number, goalMet: boolean, interrupted: boolean): CompletionBucket {
  if (interrupted) return "interrupted";
  if (durationMin >= 1080) return "deep"; // 18h+
  if (goalMet) return "goal_met";
  if (durationMin < 360) return "short"; // <6h
  return "standard";
}

function pick<T>(list: T[], seed: number): T | undefined {
  if (list.length === 0) return undefined;
  return list[Math.abs(Math.floor(seed)) % list.length];
}

export interface CompletionSelection {
  atmosphere?: CompletionMessage;
  benefit?: CompletionMessage;
  bucket: CompletionBucket;
}

export function selectCompletionMessages(opts: {
  durationMinutes: number;
  goalMinutes: number;
  goalMet: boolean;
  interrupted?: boolean;
  seed?: number;
  includeAtmosphere?: boolean;
  includeBenefit?: boolean;
}): CompletionSelection {
  const {
    durationMinutes,
    goalMet,
    interrupted = false,
    seed = Date.now(),
    includeAtmosphere = true,
    includeBenefit = true,
  } = opts;

  const bucket = bucketFor(durationMinutes, goalMet, interrupted);

  const eligible = (type: CompletionType) =>
    ALL.filter(
      (m) =>
        m.type === type &&
        m.bucket === bucket &&
        durationMinutes >= m.minDurationMinutes &&
        durationMinutes <= m.maxDurationMinutes &&
        (!m.requiresGoalMet || goalMet)
    );

  // Fallback to standard bucket if a specific bucket has no entries
  const withFallback = (type: CompletionType) => {
    const primary = eligible(type);
    if (primary.length > 0) return primary;
    return ALL.filter(
      (m) => m.type === type && m.bucket === "standard"
    );
  };

  return {
    bucket,
    atmosphere: includeAtmosphere ? pick(withFallback("atmosphere"), seed) : undefined,
    benefit: includeBenefit ? pick(withFallback("benefit"), seed + 7) : undefined,
  };
}
